import useNotifications from 'core/notifications/useNotifications';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router';
import { IOffsiteTemplatePreviewResponse } from 'sections/design/template-management/entities/templates';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { offsitePreviewTemplate, OffsitePreviewTemplateRequest } from '../utils/queries';
import { TemplateType } from 'models/template/Template';

export const useOffsitePreview = () => {
  const { t } = useTranslation('designs');
  const { showNotification } = useNotifications();

  const { mutate: preview } = useMutation(offsitePreviewTemplate);

  const dispatch = useTemplateEditorDispatch();
  const { template, linkLookups, compilationContext } = useTemplateEditorState();

  const { siteId, language } = useParams<{
    siteId: string;
    id: string;
    channel: string;
    language: string;
  }>();

  return useCallback(() => {
    const precompiledTemplate = template?.template ?? '';

    const payload: OffsitePreviewTemplateRequest = {
      payload: {
        template: {
          type: 'body',
          value: precompiledTemplate,
        },
        channel: template.channel,
        featureType: template?.type,
        bulkFeatureType: template?.type === TemplateType.BulkRetargeting ? TemplateType.CartAbandonment : undefined,
        linkConfig: linkLookups,
        compilationContext: compilationContext.compilationContext,
        language,
        ids: {
          siteId: siteId,
          compilationId: 'b283a6cd-56e8-4f27-9f30-e87488098664',
          snapshotId: 'b283a6cd-56e8-4f27-9f30-e87488098664',
          machineGuid: 'b283a6c,d-56e8-4f27-9f30-e87488098664',
        },
        lookbackPeriod: 1440, // 24 hours in minutes
      },
      t,
    };

    const onSuccess = (data: IOffsiteTemplatePreviewResponse) => {
      localStorage.setItem('templateEditorPreview', data.compiled);
      dispatch({
        type: 'UPDATE_PREVIEW_INITIAL',
        payload: {
          precompiledTemplate: data.precompiled,
          compiledTemplate: data.compiled,
          precompiledLinks: data.precompiledLinks,
          transformedLinks: data.transformedLinks,
          lastCompiledCompilationContext: compilationContext,
          lastCompiledTemplateBody: data.precompiled,
        },
      });

      if (template.template !== data.precompiled) {
        dispatch({
          type: 'UPDATE_TEMPLATE',
          payload: {
            precompiledTemplate: data.precompiled,
          },
        });
      }
    };

    const onError = (error: Error) => {
      showNotification(error.message, 'error');
    };

    preview(payload, { onSuccess, onError });
  }, [
    compilationContext,
    dispatch,
    language,
    linkLookups,
    preview,
    showNotification,
    siteId,
    t,
    template.channel,
    template?.template,
    template?.type,
  ]);
};
